/**
 * «Hola Donna»: con el interruptor encendido, Donna escucha en esta pestaña y actúa solo ante frases que
 * empiezan con «Hola Donna». Las órdenes navegan, buscan o abren el dictado; nunca firman, guardan,
 * cobran ni borran. Se pausa mientras hay un dictado y cuando la pestaña no está a la vista.
 */
import { useEffect, useRef, useState } from "react";
import { api, leerSesion, puede, type Paciente } from "../api.ts";
import { interpretarOrden, pacienteDicho, respuestaOrden, trasActivacion, habladoOrden, habladoApertura, habladoVarios, habladoNoEncontrado, type Orden, type Pestana } from "../../../dominio/src/index.ts";
import { crearReconocedor, hayDictadoActivo, EV_DICTADO, EV_ORDEN, EV_DICTAR, EV_DETENER, type Reconocedor } from "../voz.ts";
import { hablar, azar, leerAjustes, EV_HABLA } from "../habla.ts";
import { resumenDeHoy } from "../resumenDia.ts";

const CLAVE = "donna.holaDonna";
const leer = () => { try { return localStorage.getItem(CLAVE) === "1"; } catch { return false; } };
const PERMISO_DESTINO: Record<string, string | null> = { inicio: null, agenda: "agenda:leer", mensajes: "agenda:leer", cumpleanos: "agenda:leer", configuracion: null, respaldos: "respaldos:admin", reportes: "reportes:leer", facturas: "caja:leer", nuevo: "pacientes:escribir" };

export const soportaVoz = () => typeof window !== "undefined" && !!((window as unknown as Record<string, unknown>).SpeechRecognition ?? (window as unknown as Record<string, unknown>).webkitSpeechRecognition);

export function HolaDonna({ ruta, ir }: { ruta: string[]; ir: (r: string) => void }) {
  const [activo, setActivo] = useState(leer);
  const [oyendo, setOyendo] = useState(false);
  const [mensaje, setMensaje] = useState<{ texto: string; hasta: number } | null>(null);
  const rutaRef = useRef(ruta); rutaRef.current = ruta;
  const rec = useRef<Reconocedor | null>(null);
  const quiere = useRef(false); // debería estar escuchando
  const esperandoOrden = useRef(0); // dijo solo «Hola Donna»: la próxima frase es la orden
  const opciones = useRef<{ lista: Paciente[]; pestana?: Pestana; hasta: number } | null>(null); // varios pacientes: «abre el segundo»

  /** Lo muestra en pantalla y lo dice en voz alta (si es corto; los textos largos solo se escriben, salvo que se dé una versión para la voz). */
  const decir = (texto: string, ms = 4500, voz?: string) => {
    setMensaje({ texto, hasta: Date.now() + ms });
    const hablado = voz ?? (texto.length <= 140 ? texto : "");
    if (hablado) void hablar(hablado);
  };
  const ctxVoz = () => ({ azar, nombre: leerSesion()?.usuario.nombre, discreto: leerAjustes().discreto });
  useEffect(() => { if (!mensaje) return; const t = setTimeout(() => setMensaje(null), Math.max(0, mensaje.hasta - Date.now())); return () => clearTimeout(t); }, [mensaje]);

  function ejecutar(o: Orden) {
    const [pantalla, id] = rutaRef.current;
    const pacienteId = (pantalla === "paciente" || pantalla === "expediente") && id ? id : null;
    switch (o.tipo) {
      case "ir": {
        const permiso = PERMISO_DESTINO[o.destino];
        if (permiso && !puede(permiso)) return decir("Tu rol no tiene acceso a esa sección.");
        ir(o.destino === "cumpleanos" ? "mensajes/cumpleanos" : o.destino); break;
      }
      case "pestana":
        if (!pacienteId) return decir("Primero abre un paciente. Por ejemplo: «Hola Donna, busca a Lucía Paredes».");
        ir(`paciente/${pacienteId}/${o.pestana}`); break;
      case "buscar": { buscarYAbrir(o.texto, o.pestana); return; }
      case "elegir": {
        const op = opciones.current;
        if (!op || op.hasta < Date.now()) return decir("No tengo una lista de pacientes para elegir. Di, por ejemplo: «Hola Donna, abre a Lucía Paredes».");
        const x = op.lista[o.n - 1];
        if (!x) return decir(`Solo hay ${op.lista.length} en la lista.`);
        abrirPaciente(x, op.pestana); return;
      }
      case "dictar": {
        const ev = new CustomEvent(EV_DICTAR, { cancelable: true });
        window.dispatchEvent(ev);
        if (!ev.defaultPrevented) {
          if (!pacienteId || !puede("clinico:escribir")) return decir("Abre un paciente para dictar su consulta.");
          ir(`paciente/${pacienteId}/consulta`);
          setTimeout(() => window.dispatchEvent(new CustomEvent(EV_DICTAR, { cancelable: true })), 600);
        }
        break;
      }
      case "detener_dictado": window.dispatchEvent(new Event(EV_DETENER)); break;
      case "resumen_dia":
        decir("Un momento…", 3000, habladoOrden(o, ctxVoz()));
        resumenDeHoy().then((t) => decir(t, 16000, t)).catch(() => decir("No pude leer tu agenda en este momento. Intenta de nuevo."));
        return;
      case "historia_pdf":
        if (!pacienteId || !puede("clinico:leer")) return decir("Abre un paciente para imprimir su historia.");
        if (pantalla === "expediente") window.print(); else location.hash = `#/expediente/${pacienteId}?imprimir`;
        break;
      default: break;
    }
    decir(respuestaOrden(o), o.tipo === "ayuda" ? 12000 : 4500, habladoOrden(o, ctxVoz()));
  }
  const ejecutarRef = useRef(ejecutar); ejecutarRef.current = ejecutar;

  function abrirPaciente(x: Paciente, pestana?: Pestana) {
    opciones.current = null;
    ir(`paciente/${x.id}${pestana ? `/${pestana}` : ""}`);
    decir(`Abro ${pestana ? respuestaOrden({ tipo: "pestana", pestana }).replace(/^Abro |\.$/g, "") + " de " : "a "}${x.nombres} ${x.apellidos} (HC ${x.hc}).`, 4500, habladoApertura({ ...ctxVoz(), pestana, nombrePaciente: `${x.nombres} ${x.apellidos}` }));
  }
  /** Busca como el buscador del inicio y, si queda claro quién es, abre su ficha; si hay varios, los lee para elegir. */
  async function buscarYAbrir(texto: string, pestana?: Pestana) {
    decir(`Busco a «${texto}»…`, 4500, habladoOrden({ tipo: "buscar", texto }, ctxVoz()));
    let lista: Paciente[];
    try { lista = await api<Paciente[]>("GET", `/api/pacientes?q=${encodeURIComponent(texto)}`); }
    catch { return decir("No pude buscar en este momento. Intenta de nuevo."); }
    if (!lista.length) { ir(`inicio?q=${encodeURIComponent(texto)}`); return decir(`No encontré a «${texto}». Revisa el nombre o dilo con apellido.`, 6000, habladoNoEncontrado({ ...ctxVoz(), texto })); }
    const elegido = pacienteDicho(texto, lista);
    if (elegido) return abrirPaciente(elegido, pestana);
    const primeros = lista.slice(0, 5);
    opciones.current = { lista: primeros, pestana, hasta: Date.now() + 120000 };
    ir(`inicio?q=${encodeURIComponent(texto)}`);
    decir(`Encontré ${lista.length} pacientes: ${primeros.map((x, i) => `${i + 1}) ${x.nombres} ${x.apellidos}`).join(", ")}${lista.length > 5 ? "…" : ""}. Di «Hola Donna, abre el primero», «el segundo»…`, 15000, habladoVarios({ ...ctxVoz(), total: lista.length, nombres: primeros.map((x) => `${x.nombres} ${x.apellidos}`) }));
  }

  function oir(frase: string) {
    const tras = trasActivacion(frase);
    if (tras === null) {
      // pausa después de «Hola Donna»: se acepta la frase siguiente como orden durante 6 segundos
      if (esperandoOrden.current > Date.now()) { esperandoOrden.current = 0; ejecutarRef.current(interpretarOrden(frase)); }
      return;
    }
    if (!tras) { esperandoOrden.current = Date.now() + 6000; decir("Te escucho…", 6000, habladoOrden({ tipo: "desconocida", texto: "" }, ctxVoz())); return; }
    ejecutarRef.current(interpretarOrden(tras));
  }

  function arrancar() {
    if (!soportaVoz() || rec.current || hayDictadoActivo() || document.hidden) return;
    const r = crearReconocedor();
    if (!r) return;
    r.lang = "es-EC"; r.continuous = true; r.interimResults = false;
    r.onresult = (e) => { for (let i = e.resultIndex; i < e.results.length; i++) if (e.results[i].isFinal) oir(e.results[i][0].transcript); };
    r.onerror = (e) => {
      if (e.error === "not-allowed" || e.error === "service-not-allowed") {
        quiere.current = false; setActivo(false); guardar(false);
        decir("Permite el micrófono en este navegador para usar «Hola Donna».", 8000);
      }
    };
    // el navegador corta la escucha cada cierto tiempo: se reanuda mientras siga encendido
    r.onend = () => { rec.current = null; setOyendo(false); if (quiere.current) setTimeout(arrancar, 300); };
    rec.current = r;
    try { r.start(); setOyendo(true); } catch { rec.current = null; }
  }
  function parar() { const r = rec.current; rec.current = null; setOyendo(false); if (r) { r.onend = null; try { r.stop(); } catch { /* ya estaba detenido */ } } }
  const guardar = (v: boolean) => { try { localStorage.setItem(CLAVE, v ? "1" : "0"); } catch { /* opcional */ } };

  useEffect(() => {
    quiere.current = activo;
    if (activo) arrancar(); else parar();
    const alDictar = (e: Event) => { if ((e as CustomEvent).detail?.activo) parar(); else if (quiere.current) setTimeout(arrancar, 400); };
    const alVisible = () => { if (document.hidden) parar(); else if (quiere.current) arrancar(); };
    // mientras Donna habla, el micrófono se pausa para que no se escuche a sí misma
    const alHabla = (e: Event) => { if ((e as CustomEvent).detail?.activo) parar(); else if (quiere.current && !hayDictadoActivo()) setTimeout(arrancar, 600); };
    const alOrden = (e: Event) => { const t = String((e as CustomEvent).detail?.texto ?? ""); ejecutarRef.current(interpretarOrden(t)); };
    window.addEventListener(EV_DICTADO, alDictar);
    window.addEventListener(EV_HABLA, alHabla);
    document.addEventListener("visibilitychange", alVisible);
    window.addEventListener(EV_ORDEN, alOrden);
    return () => { window.removeEventListener(EV_DICTADO, alDictar); window.removeEventListener(EV_HABLA, alHabla); document.removeEventListener("visibilitychange", alVisible); window.removeEventListener(EV_ORDEN, alOrden); parar(); };
  }, [activo]);

  // si la persona lo pidió, al entrar saluda y cuenta cómo va el día (una vez al día; si el navegador no la deja hablar todavía, lo hace al primer toque)
  useEffect(() => {
    const aj = leerAjustes();
    const usuario = leerSesion()?.usuario.id;
    if (!aj.saludar || !aj.habla || !usuario || !puede("agenda:leer")) return;
    const clave = "donna.saludo." + usuario, hoyLocal = new Date().toLocaleDateString("sv-SE");
    try { if (localStorage.getItem(clave) === hoyLocal) return; } catch { return; }
    const marcar = () => { try { localStorage.setItem(clave, hoyLocal); } catch { /* sin almacenamiento */ } };
    let cancelado = false, alToque: (() => void) | null = null;
    resumenDeHoy().then(async (t) => {
      if (cancelado) return;
      if (await hablar(t)) { marcar(); decir(t, 12000, ""); return; }
      alToque = async () => { alToque = null; if (!cancelado && (await hablar(t))) { marcar(); decir(t, 12000, ""); } };
      window.addEventListener("pointerdown", alToque, { once: true });
    }).catch(() => undefined);
    return () => { cancelado = true; if (alToque) window.removeEventListener("pointerdown", alToque); };
  }, []);

  if (!soportaVoz()) return null;
  function alternar() {
    const v = !activo;
    setActivo(v); guardar(v);
    if (v) void hablar("Listo, te escucho. Cuando quieras, di «Hola Donna» y lo que necesites.");
    decir(v
      ? "«Hola Donna» encendido: escucho en esta pestaña y solo actúo cuando dices «Hola Donna». El reconocimiento de voz del navegador procesa lo que oye (en Chrome, un servicio de Google). Por seguridad no firmo, guardo ni cobro por voz. Di «Hola Donna, ayuda» para ver las órdenes."
      : "«Hola Donna» apagado: el micrófono queda cerrado.", v ? 12000 : 3000);
  }
  return (
    <>
      <button className={`btn chico hola-donna${activo ? " on" : ""}`} onClick={alternar} aria-pressed={activo}
        title={activo ? "«Hola Donna» encendido: toca para apagar" : "Encender «Hola Donna» (órdenes por voz)"}>
        <span className={`punto${oyendo ? " vivo" : ""}`} aria-hidden="true" />Hola Donna
      </button>
      {mensaje && <div className="donna-voz" role="status" aria-live="polite"><b>Donna:</b> {mensaje.texto}</div>}
    </>
  );
}
